import React, { useState, useEffect } from "react";
import {
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  FlatList,
  Platform,
  Dimensions
} from "react-native";

import { findIndex } from "lodash";
import moment from "moment";

import { getTimeFormat } from "../../../../helpers/utils";
import appStyles from "../../../appStyles";
import AppTheme from "../../AppTheme";

import {
  RecyclerListView,
  DataProvider,
  LayoutProvider,
} from "recyclerlistview";

const { height: HEIGHT } = Dimensions.get("window");
const USE_RECYCLER_LIST = true;

export default function TimePickerQuickEntry({
  startTime,
  endTime,
  timeslotDate,
  isStartTime,
  use24hFormat,
  minutesInterval,
  onTimeChanged = () => {},
  showTutorials = false,
  disablePills = false,
  dialogOffset= 400
}) {
  const [highlightedEndTimeId, setHighlightedEndTimeId] = useState();

  const [intervalValues, setIntervalValues] = useState(null);
  const [initialScrollIndex, setInitialScrollIndex] = useState(-1);
  const [selectedId, setSelectedId] = useState(null);
  const timeFormat = getTimeFormat(use24hFormat);
  const shortFormat = use24hFormat ? "HH:mm" : "h:mm";

  useEffect(() => {
    initIntervalValues();
  }, []);

  const initIntervalValues = () => {
    const start = moment(
      `${timeslotDate} ${startTime}`,
      "YYYY-MM-DD " + timeFormat,
    );
    const end = moment(
      `${timeslotDate} ${endTime}`,
      "YYYY-MM-DD " + timeFormat,
    );

    if (end < start) end.add(1, "days");

    const selectedMoment = isStartTime ? start.clone() : end.clone();
    const selectedMomentEnd = selectedMoment.clone().add(minutesInterval, "minutes");
    const selectedMomentEnd2 = selectedMoment.clone().add(2 * minutesInterval, "minutes");

    // Show 5 min increments 30 minutes before start time
    const start5MinutesPoint = isStartTime ? start.clone().add(-30, "minutes") : start.clone();

    if (isStartTime) {
      // For start-time allow user to go 23h into the past + Reset minutes to 0
      start.add(-23, "hours").minutes(0);
    } else {
      // Dont show start time
      start.add(5, "minutes");

      // For end-time allow user to go 23h into the future
      end.add(23, "hours");
    }

    // Tutorials
    if (showTutorials && !disablePills && !isStartTime) {
      setHighlightedEndTimeId(
        selectedMoment.clone().add(15, "minutes").format("YYYYMMDDHHmm"),
      );
    }

    let index = -1;
    let prevList = [];
    let afterList = [];
    while (start < end) {
      index++;
      let model = getIntervalModel(start);

      if (start < selectedMoment) {
        prevList.push(model);
      } else if (start > selectedMoment) {
        model.isDisabled = disablePills || (showTutorials && isStartTime);
        afterList.push(model);
      }

      const interval = (
        isStartTime
          ? start >= start5MinutesPoint
          : start < selectedMoment ||
            start < selectedMomentEnd ||
            (start < selectedMomentEnd2 &&
              start.minutes() % minutesInterval != 0)
      )
        ? Math.min(minutesInterval, 5)
        : minutesInterval || 15;

      start.add(interval, "minutes");
    }

    const selectedIntervalModel = getIntervalModel(selectedMoment);

    // After splitting a normal interval (ex. 7:45-7:55), include abnormal times in list
    const allIntervals = [...prevList, selectedIntervalModel, ...afterList];

    const foundIndex = findIndex(
      allIntervals,
      (t) => t.id == selectedIntervalModel.id,
    );

    setInitialScrollIndex(foundIndex);
    setSelectedId(selectedIntervalModel.id);
    setIntervalValues(allIntervals);
  };

  const getIntervalModel = (selectedDate) => {
    const time = selectedDate.format("YYYY-MM-DD " + timeFormat);
    return {
      id: selectedDate.format("YYYYMMDDHHmm"),
      time,
      hhmma: selectedDate.format(timeFormat),
      hhmm: selectedDate.format(shortFormat),
      meridian: use24hFormat ? "" : selectedDate.format("a"),
      isDisabled: disablePills,
    };
  };

  const onItemSelected = (item) => {
    // Tutorials
    if (item.isDisabled) return;
    if (highlightedEndTimeId && highlightedEndTimeId != item.id) return;

    setSelectedId(item.id);
    onTimeChanged(item.time, isStartTime);
  };

  const renderItem = ({ item }) => {
    const isSelected = item.id == selectedId;

    return (
      <View
        style={{
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
        }}
      >
        <TouchableOpacity
          style={[
            styles.intervalItem,
            isSelected ? styles.selected : {},
            highlightedEndTimeId == item.id ? appStyles.highlight : {},
            item.isDisabled ? { opacity: 0.2 } : {},
          ]}
          onPress={() => onItemSelected(item)}
          key={item.id}
        >
          <Text
            style={[
              styles.intervalItemText,
              isSelected ? styles.selectedText : {},
            ]}
          >
            {item.hhmm}
          </Text>
          <Text
            style={[styles.meridian, isSelected ? styles.selectedText : {}]}
          >
            {item.meridian}
          </Text>
        </TouchableOpacity>
      </View>
    );
  };

  if (!intervalValues) return null;

  // ------------------------------------- SCROLL TO -------------------------------------
  const titleH = 54;
  const listHeight = Math.min(570, HEIGHT - dialogOffset) - titleH;
  const itemsPerPage = listHeight / 48;
  const offset = Math.round(itemsPerPage / 2);
  const scrollIndex = Math.max(0, initialScrollIndex - offset);
  // ------------------------------------- END SCROLL TO ---------------------------------

  const dataProvider = USE_RECYCLER_LIST
    ? new DataProvider((r1, r2) => {
        return r1 !== r2;
      }).cloneWithRows(intervalValues)
    : null;
  const layoutProvider = new LayoutProvider(
    () => 0,
    (type, dim) => {
      dim.width = 170;
      dim.height = 42;
    },
  );
  const rowRenderer = (type, data, index) => {
    return renderItem({ item: data, index });
  };

  return (
    <View style={[styles.container, isStartTime && styles.border]}>
      {USE_RECYCLER_LIST ? (
        <RecyclerListView
          style={styles.list}
          isHorizontal={false}
          useWindowScroll={true}
          rowRenderer={rowRenderer}
          dataProvider={dataProvider}
          layoutProvider={layoutProvider}
          renderAheadOffset={0}
          scrollViewProps={{
            overflow: "hidden",
            showsHorizontalScrollIndicator: true,
            showsVerticalScrollIndicator: true,
          }}
          contentContainerStyle={styles.contentContainerStyle}
          initialRenderIndex={scrollIndex}
          keyboardShouldPersistTaps="handled"
        />
      ) : (
        <FlatList
          style={styles.list}
          contentContainerStyle={styles.contentContainerStyle}
          data={intervalValues}
          initialScrollIndex={scrollIndex}
          keyExtractor={(item) => item.id}
          renderItem={renderItem}
          extraData={selectedId}
          getItemLayout={(data, index) => ({
            offset: 42 * index,
            length: 42,
            index,
          })}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    height: "100%",
    paddingVertical: 10,
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  border: {
    borderRightWidth: 1,
    borderRightColor: "#F5F5F5",
  },
  list: {
    width: "100%",
  },
  contentContainerStyle: {
    width: "100%",
    justifyContent: "center",
    alignItems: "center",
  },
  intervalItem: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    marginTop: 6,
    backgroundColor: "#F4F6FA",
    borderRadius: 4,
    width: 80,
    height: 36,
  },
  intervalItemText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 16,
    lineHeight: 20,
    letterSpacing: 1,
    color: AppTheme.almostBlack,
  },
  meridian: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 7,
    lineHeight: 9,
    letterSpacing: 0.17,
    color: "#2D3142",
    textTransform: "lowercase",
    paddingTop: 5,
  },
  selected: {
    backgroundColor: AppTheme.DEFAULT_COLOR,
  },
  selectedText: {
    color: "#FFF",
  },
});
